import { Box, Flex, Grid, GridItem, Text } from "@chakra-ui/react";
import type { BoxProps } from "@chakra-ui/react";

export interface MetricItem {
  /** Display label (e.g. "Spend", "ROAS") */
  label: string;
  /** Display value; can be string or React node for flexibility */
  value: React.ReactNode;
  /** Optional id for list key when order is stable */
  id?: string;
}

export interface MetricsCardProps extends Omit<BoxProps, "children"> {
  /** List of label/value pairs to show */
  items: MetricItem[];
  /** Number of grid columns (default: 4) */
  columns?: number;
  /** Gap between items (default: 4) */
  gap?: number | string;
  /** Font size for labels */
  labelFontSize?: string | number;
  /** Font size for values */
  valueFontSize?: string | number;
  /** Optional content rendered at the bottom of the card, edge-aligned (e.g. status bar with actions) */
  children?: React.ReactNode;
  /** Function to format values */
  formatValue?: (value: React.ReactNode) => string;
}

/** Round numeric values to max 2 decimals; leave pre-formatted strings (e.g. "$1.00", "1.5%") as-is. */
function defaultFormatValue(value: React.ReactNode): string {
  if (typeof value === "number") {
    return value.toFixed(2);
  }
  const s = value?.toString?.() ?? "";
  if (typeof s !== "string") return String(value);
  const n = parseFloat(s);
  // Only round when the value is a bare number (no currency/unit prefix or suffix)
  if (!Number.isNaN(n) && s.trim() === String(n)) {
    return n.toFixed(2);
  }
  return s;
}

export default function MetricsCard({
  items,
  columns = 4,
  gap = 6,
  labelFontSize = "sm",
  valueFontSize = "2xl",
  bg = "white",
  borderRadius = "2xl",
  borderWidth = "1px",
  borderColor = "gray.200",
  p = 4,
  children,
  formatValue = defaultFormatValue,
  ...boxProps
}: MetricsCardProps) {
  // When a cell value is empty or "—", give it minimal width so other cells take the space
  const templateColumns = items
    .map((item) => {
      const formatted = formatValue(item.value);
      const isEmpty =
        formatted === "" ||
        formatted === "—" ||
        String(formatted).trim() === "";
      return isEmpty ? "minmax(70px, auto)" : "minmax(90px, 1fr)";
    })
    .join(" ");

  return (
    <Box
      bg={bg}
      borderRadius={borderRadius}
      borderWidth={borderWidth}
      borderColor={borderColor}
      // p={p}
      {...boxProps}
    >
      <Flex direction="column" gap={0}>
        <Box overflowX="auto" overflowY="hidden" minW={0} w="100%">
          <Grid templateColumns={templateColumns} rowGap={0} w="100%">
            {items.map((item, index) => (
              <GridItem
                key={item.id ?? index}
                alignItems="center"
                justifyContent="center"
                textAlign="center"
                gap={0}
                p={p}
                borderRightWidth={index < items.length - 1 ? "1px" : 0}
                borderRightStyle="solid"
                borderRightColor="gray.200"
              >
                <Text
                  fontSize={labelFontSize}
                  lineHeight="130%"
                  color="gray.500"
                  fontFamily="Work Sans"
                  fontWeight={600}
                  noOfLines={1}
                >
                  {item.label}
                </Text>
                <Text
                  fontSize={valueFontSize}
                  lineHeight="130%"
                  fontWeight={400}
                  color="gray.600"
                  fontFamily="Work Sans"
                >
                  {formatValue(item.value)}
                </Text>
              </GridItem>
            ))}
          </Grid>
        </Box>
        {children ? (
          <Box
            w="100%"
            alignSelf="stretch"
            borderTopWidth="1px"
            borderTopStyle="solid"
            borderTopColor="gray.200"
            p={3}
          >
            {children}
          </Box>
        ) : null}
      </Flex>
    </Box>
  );
}
